"use client";

/**
 * O que a pessoa vê quando a app rebenta.
 *
 * Até aqui era o ecrã branco do Next: sem explicação, sem saída, e sem nada
 * que chegasse a quem pode resolver. Este componente troca isso por três
 * coisas — uma frase honesta, uma referência para citar, e um botão para sair
 * de onde se está.
 *
 * ─── Nada segue daqui sem a pessoa mandar ─────────────────────────────────
 *
 * Este ecrã já enviou o report sozinho, mal montava, e mostrava a referência
 * como facto consumado. Deixou de o fazer, por uma razão que não é técnica:
 * quem acaba de levar com um erro quer EXPLICAR o que estava a fazer, e um
 * ecrã que o regista antes de perguntar tira-lhe isso — passa-lhe a mensagem
 * de que a opinião dele não fazia falta.
 *
 * O que se perde: um crash em que a pessoa fecha o separador sem escrever nada
 * deixa de chegar cá. Não é pouco, e foi aceite de olhos abertos — em troca,
 * cada report que chega traz sempre a metade que nenhuma recolha automática
 * consegue produzir. A outra metade não se perde: o stack, a rota, o rasto de
 * cliques e a versão do build continuam a ir todos, montados por
 * `submitBugReport` sem a pessoa ter de os pedir.
 *
 * O ecrã também não anuncia recolha nenhuma. Descrever a captura no instante
 * em que acontece transforma um contratempo numa notificação de telemetria, e
 * não dá a quem está do outro lado nada que ele possa decidir. Pela mesma
 * razão não se mostra a contagem de ocorrências: essa ordena a fila do IT, não
 * é assunto de quem apanhou o erro.
 *
 * Depende de propósito de muito pouco: elementos simples e classes de tokens.
 * Este é o código que corre quando o resto está partido, e cada import é mais
 * uma coisa que pode estar partida no caminho de contar que se partiu.
 * Nomeadamente: nada de `@/components/ui/*` aqui.
 *
 * A canvas para este componente é tempo/designs/design-system/manual/index.canvas.tsx.
 * Se alterar este componente de alguma forma, garanta que a canvas e a sua
 * declaração de asset se mantêm consistentes — incluindo as duas decisões
 * acima, que estão lá escritas como regra e não como história.
 */
import { useState } from "react";
import { submitBugReport, type BugReportResult } from "@/lib/bug-report-client";

interface CrashScreenProps {
  error: Error & { digest?: string };
  /** Do error boundary do React — volta a montar a árvore que rebentou. */
  reset?: () => void;
  /** Ausente no `global-error`, onde não há layout para onde voltar. */
  homeHref?: string;
}

export function CrashScreen({ error, reset, homeHref }: CrashScreenProps) {
  const [result, setResult] = useState<BugReportResult | null>(null);
  const [state, setState] = useState<"por-enviar" | "a-enviar" | "enviado" | "falhou">(
    "por-enviar",
  );
  const [note, setNote] = useState("");
  const [copied, setCopied] = useState(false);

  const send = async () => {
    if (!note.trim() || state === "a-enviar") return;
    setState("a-enviar");

    // O texto é o que a PESSOA acrescenta; o stack, a rota, o rasto de cliques
    // e a versão do build vão na mesma carga, montados pelo cliente de envio.
    // É essa combinação que faz um report valer: "não deu para guardar" sozinho
    // não localiza nada, e um stack sozinho não diz o que se estava a tentar.
    const r = await submitBugReport({
      kind: "crash",
      error,
      userDescription: note.trim(),
    });

    if (r) {
      setResult(r);
      setState("enviado");
    } else {
      setState("falhou");
    }
  };

  const copyRef = () => {
    if (!result?.ref) return;
    navigator.clipboard?.writeText(result.ref).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => {
        /* clipboard negado — a referência está à vista para copiar à mão */
      },
    );
  };

  return (
    // `min-h-full` e não `min-h-screen`: sob `/[tenant]/**` este ecrã aparece
    // DENTRO do layout, num `<main>` que já perdeu a altura do cabeçalho para
    // ele. Pedir 100vh aí pede mais do que o sítio tem, e sobrava exactamente a
    // altura do cabeçalho em scroll — uma barra a deslizar sobre um cartão que
    // cabia inteiro no ecrã. O `global-error` não tem layout nenhum, e é por
    // isso que o `<body>` de lá leva o `min-h-screen` que aqui saiu.
    <div className="flex min-h-full items-center justify-center bg-background p-d6">
      <div className="w-full max-w-lg rounded-xl border border-border bg-card p-d8 shadow-e1">
        <div className="mb-d5 flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="text-destructive"
            aria-hidden="true"
          >
            <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
          </svg>
        </div>

        <h1 className="mb-d2 text-xl font-semibold text-card-foreground">
          Alguma coisa correu mal
        </h1>

        {/*
          Sem "tente novamente mais tarde" nem "erro inesperado": não dizem
          nada e soam a desculpa. E sem anunciar recolha nenhuma — nada segue
          daqui sem a pessoa carregar em Enviar.
        */}
        <p className="mb-d6 text-sm leading-relaxed text-muted-foreground">
          Este ecrã não conseguiu carregar. Pode tentar de novo ou voltar ao
          início. Se quiser que a equipa de IT trate disto, conte-nos o que
          estava a fazer.
        </p>

        {/* ── O relato ── */}
        {(state === "por-enviar" || state === "a-enviar" || state === "falhou") && (
          <div className="mb-d6">
            <label
              htmlFor="bug-note"
              className="mb-d2 block text-sm text-muted-foreground"
            >
              O que estava a fazer?
            </label>
            <textarea
              id="bug-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              autoFocus
              maxLength={5000}
              className="w-full resize-none rounded-lg border border-border bg-background px-d3 py-d2 text-sm text-foreground outline-none transition-colors focus:border-primary"
              // "Ex:" à cabeça porque sem ele o placeholder lê-se como texto
              // já escrito na caixa — e quem hesita a reportar hesita ainda
              // mais quando não percebe se aquilo é dele ou é sugestão.
              placeholder="Ex: Estava a guardar uma lead e…"
            />
            <button
              type="button"
              onClick={send}
              disabled={!note.trim() || state === "a-enviar"}
              className="mt-d2 rounded-md border border-border px-d3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-40"
            >
              {state === "a-enviar" ? "A enviar…" : "Enviar"}
            </button>
          </div>
        )}

        {state === "enviado" && result?.ref && (
          <div className="mb-d6 rounded-lg border border-border bg-muted/40 px-d4 py-d3">
            <div className="mb-d1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Referência
            </div>
            <div className="flex items-center gap-d3">
              <code className="font-mono text-base font-semibold text-card-foreground">
                {result.ref}
              </code>
              <button
                type="button"
                onClick={copyRef}
                className="rounded-md border border-border px-d2 py-d1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                {copied ? "Copiado" : "Copiar"}
              </button>
            </div>
            <p className="mt-d2 text-xs text-muted-foreground">
              Guarde-a — é por ela que o problema é seguido.
            </p>
          </div>
        )}

        {state === "falhou" && (
          <div className="mb-d6 rounded-lg border border-border bg-muted/40 px-d4 py-d3 text-sm text-muted-foreground">
            {/*
              O texto continua na caixa e o botão continua activo: a falha aqui
              é quase sempre de rede, e apagar o que a pessoa escreveu por causa
              de uma ligação intermitente é a melhor forma de ela não voltar a
              tentar.
            */}
            Não foi possível enviar — provavelmente falta ligação. O que escreveu
            continua aí; tente outra vez.
            {/*
              O `digest` é o identificador que o próprio Next atribui ao erro e
              regista nos logs do servidor. Quando o envio falha, é o único fio
              que sobra para ligar este ecrã a alguma coisa.
            */}
            {error.digest && (
              <div className="mt-d2 font-mono text-xs">
                digest: {error.digest}
              </div>
            )}
          </div>
        )}

        {/* ── Saídas ── */}
        <div className="flex gap-d3">
          {reset && (
            <button
              type="button"
              onClick={reset}
              className="rounded-lg bg-primary px-d4 py-d2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
            >
              Tentar novamente
            </button>
          )}
          <a
            href={homeHref ?? "/"}
            className="rounded-lg border border-border px-d4 py-d2 text-sm font-medium text-card-foreground transition-colors hover:bg-accent"
          >
            Voltar ao início
          </a>
        </div>
      </div>
    </div>
  );
}
